import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { GasBadge, GasButton, GasEmptyState, useGasI18n } from '../../shared/kit';
import {
    trackDirectoryPageView,
    trackDirectorySearch,
    trackMemberClick,
} from '../member-search/analytics';
import { fetchPublicMembers, fetchPublicMembersSuggestions } from './api';
import type {
    PublicMemberRow,
    PublicMembersFilters,
    PublicMembersListInitData,
    PublicMembersSuggestion,
} from './types';

interface AppProps {
    initData: PublicMembersListInitData;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100, -1];

const DEFAULT_ORDER: PublicMembersFilters['order_direction'] = 'ASC';

export default function App({ initData }: AppProps) {
    const { t, tn } = useGasI18n();
    const allCantons = React.useMemo(
        () => initData.cantons.map((canton) => canton.code),
        [initData.cantons],
    );
    const [filters, setFilters] = React.useState<PublicMembersFilters>({
        search: '',
        type_membre: '',
        genre: '',
        cantons: allCantons,
        order_by: 'prenom',
        order_direction: DEFAULT_ORDER,
    });
    const [searchDraft, setSearchDraft] = React.useState('');
    const [suggestionsOpen, setSuggestionsOpen] = React.useState(false);
    const [pageSize, setPageSize] = React.useState(-1);
    const [page, setPage] = React.useState(1);

    React.useEffect(() => {
        trackDirectoryPageView(initData.restBaseUrl, 'list', initData.restNonce, 'list_page');
    }, [initData.restBaseUrl, initData.restNonce]);

    React.useEffect(() => {
        const timer = window.setTimeout(() => {
            setFilters((current) => ({ ...current, search: searchDraft.trim() }));
            setPage(1);
        }, 250);
        return () => window.clearTimeout(timer);
    }, [searchDraft]);

    const membersQuery = useQuery({
        queryKey: ['public-members-list', filters],
        queryFn: () => fetchPublicMembers(initData.restBaseUrl, initData.restNonce, filters),
        staleTime: 60_000,
    });

    // Analytics : recherche/filtres une fois les résultats reçus.
    React.useEffect(() => {
        if (!membersQuery.isSuccess || membersQuery.data === undefined) {
            return;
        }
        const hasSearch = filters.search.trim() !== '';
        const hasType = filters.type_membre !== '';
        const hasGenre = filters.genre !== '';
        const cantonsChanged = filters.cantons.length !== allCantons.length;
        if (!hasSearch && !hasType && !hasGenre && !cantonsChanged) {
            return;
        }
        trackDirectorySearch(initData.restBaseUrl, {
            pageContext: 'list',
            query: filters.search,
            filters: {
                type_membre: filters.type_membre,
                genre: filters.genre,
                cantons: filters.cantons.length === allCantons.length ? [] : filters.cantons,
            },
            resultsCount: membersQuery.data.total ?? 0,
            source: 'filter_rail',
        }, initData.restNonce);
    }, [
        membersQuery.isSuccess,
        membersQuery.data,
        filters,
        allCantons.length,
        initData.restBaseUrl,
        initData.restNonce,
    ]);

    const suggestionsQuery = useQuery({
        queryKey: ['public-members-suggestions', searchDraft],
        queryFn: () => fetchPublicMembersSuggestions(initData.restBaseUrl, initData.restNonce, searchDraft.trim()),
        enabled: searchDraft.trim().length >= 3 && suggestionsOpen,
        staleTime: 60_000,
    });

    const rows = membersQuery.data?.rows ?? [];
    const total = membersQuery.data?.total ?? 0;
    const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(rows.length / pageSize)) : 1;
    const safePage = Math.min(page, totalPages);
    const visibleRows = pageSize > 0
        ? rows.slice((safePage - 1) * pageSize, safePage * pageSize)
        : rows;

    React.useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    function updateFilter<K extends keyof PublicMembersFilters>(key: K, value: PublicMembersFilters[K]) {
        setFilters((current) => ({ ...current, [key]: value }));
        setPage(1);
    }

    function toggleCanton(code: string) {
        const next = filters.cantons.includes(code)
            ? filters.cantons.filter((item) => item !== code)
            : [...filters.cantons, code];
        updateFilter('cantons', next);
    }

    function resetFilters() {
        setSearchDraft('');
        setFilters({
            search: '',
            type_membre: '',
            genre: '',
            cantons: allCantons,
            order_by: 'prenom',
            order_direction: DEFAULT_ORDER,
        });
        setPage(1);
        setSuggestionsOpen(false);
    }

    const typeSentence = initData.publicMemberTypes.map((type) => type.label).join(', ');

    return (
        <div className="gas-public-members">
            <header className="gas-public-members__hero">
                <div>
                    <p className="gas-public-members__eyebrow">{t('membre.public.list_eyebrow')}</p>
                    <h1>{t('membre.public.list_title')}</h1>
                    <p>{t('membre.public.list_description')}</p>
                </div>
                <a className="gas-public-members__map-link" href={initData.directoryUrl}>
                    {t('membre.public.back_to_map')}
                </a>
            </header>

            <section className="gas-public-members__filters" aria-label={t('membre.public.filters_label')}>
                <div className="gas-public-members__search">
                    <label htmlFor="gas-public-members-search">{t('membre.public.search_label')}</label>
                    <input
                        id="gas-public-members-search"
                        type="search"
                        value={searchDraft}
                        placeholder={t('membre.public.search_placeholder')}
                        autoComplete="off"
                        onFocus={() => setSuggestionsOpen(true)}
                        onChange={(event) => {
                            setSearchDraft(event.target.value);
                            setSuggestionsOpen(true);
                        }}
                    />
                    {suggestionsOpen && searchDraft.trim().length >= 3 && (
                        <SuggestionList
                            suggestions={suggestionsQuery.data ?? []}
                            loading={suggestionsQuery.isLoading}
                            onPick={(suggestion) => {
                                setSearchDraft(suggestion.label);
                                setFilters((current) => ({ ...current, search: suggestion.label }));
                                setSuggestionsOpen(false);
                            }}
                        />
                    )}
                </div>

                <div className="gas-public-members__filter-grid">
                    <label>
                        <span>{t('membre.public.type_filter_label')}</span>
                        <select
                            value={filters.type_membre}
                            onChange={(event) => updateFilter('type_membre', event.target.value)}
                        >
                            <option value="">{t('membre.public.type_filter_all')}</option>
                            {initData.publicMemberTypes.map((type) => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                        </select>
                    </label>

                    <label>
                        <span>{t('membre.public.genre_filter_label')}</span>
                        <select
                            value={filters.genre}
                            onChange={(event) => updateFilter('genre', event.target.value)}
                        >
                            <option value="">{t('membre.public.genre_filter_all')}</option>
                            <option value="homme">{t('membre.search.defaults.genre_homme')}</option>
                            <option value="femme">{t('membre.search.defaults.genre_femme')}</option>
                        </select>
                    </label>

                    <label>
                        <span>{t('membre.public.sort_label')}</span>
                        <select
                            value={`${filters.order_by}:${filters.order_direction}`}
                            onChange={(event) => {
                                const [orderBy, direction] = event.target.value.split(':');
                                setFilters((current) => ({
                                    ...current,
                                    order_by: orderBy,
                                    order_direction: direction === 'DESC' ? 'DESC' : 'ASC',
                                }));
                                setPage(1);
                            }}
                        >
                            <option value="prenom:ASC">{t('membre.public.sort_firstname')}</option>
                            <option value="nom:ASC">{t('membre.public.sort_lastname')}</option>
                            <option value="localite:ASC">{t('membre.public.sort_city')}</option>
                            <option value="canton:ASC">{t('membre.public.sort_canton')}</option>
                            <option value="type_membre:ASC">{t('membre.public.sort_type')}</option>
                        </select>
                    </label>
                </div>

                <div className="gas-public-members__cantons" aria-label={t('membre.public.cantons_filter_label')}>
                    <div className="gas-public-members__cantons-head">
                        <span>{t('membre.public.cantons_filter_label')}</span>
                        <div>
                            <button type="button" onClick={() => updateFilter('cantons', allCantons)}>
                                {t('membre.public.select_all_cantons')}
                            </button>
                            <button type="button" onClick={() => updateFilter('cantons', [])}>
                                {t('membre.public.clear_cantons')}
                            </button>
                        </div>
                    </div>
                    <div className="gas-public-members__canton-list">
                        {initData.cantons.map((canton) => (
                            <label key={canton.code} className="gas-public-members__canton">
                                <input
                                    type="checkbox"
                                    checked={filters.cantons.includes(canton.code)}
                                    onChange={() => toggleCanton(canton.code)}
                                />
                                <span>{canton.code}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="gas-public-members__actions">
                    <GasButton type="button" variant="secondary" onClick={resetFilters}>
                        {t('membre.public.reset_filters')}
                    </GasButton>
                </div>
            </section>

            <section className="gas-public-members__summary">
                <div>
                    <strong>
                        {membersQuery.isLoading
                            ? t('common.loading')
                            : total === 0
                                ? t('membre.public.no_member_found')
                                : tn('membre.public.members_found', total)}
                    </strong>
                    <p>
                        {initData.isMember
                            ? t('membre.public.member_access_hint')
                            : t('membre.public.public_access_hint', { types: typeSentence || t('membre.public.no_public_type') })}
                    </p>
                </div>
                {!initData.isMember && (
                    <a href={initData.registerUrl}>{t('membre.public.become_member')}</a>
                )}
            </section>

            {membersQuery.isError && (
                <GasEmptyState
                    title={t('membre.public.load_error_title')}
                    description={t('membre.public.load_error_description')}
                />
            )}

            {!membersQuery.isError && rows.length === 0 && !membersQuery.isLoading && (
                <GasEmptyState
                    title={t('membre.public.no_member_found')}
                    description={t('membre.public.no_member_hint')}
                />
            )}

            {rows.length > 0 && (
                <>
                    <div className="gas-public-members__toolbar">
                        <label>
                            <span>{t('membre.public.page_size_label')}</span>
                            <select
                                value={pageSize}
                                onChange={(event) => {
                                    setPageSize(Number(event.target.value));
                                    setPage(1);
                                }}
                            >
                                {PAGE_SIZE_OPTIONS.map((size) => (
                                    <option key={size} value={size}>
                                        {size === -1 ? t('membre.public.page_size_all') : String(size)}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>

                    <div className="gas-public-members__table-wrap">
                        <table className="gas-public-members__table">
                            <thead>
                                <tr>
                                    <th>{t('membre.public.col_photo')}</th>
                                    <th>{t('membre.public.col_prenom')}</th>
                                    <th>{t('membre.public.col_nom')}</th>
                                    <th>{t('membre.public.col_genre')}</th>
                                    <th>{t('membre.public.col_nom_cabinet')}</th>
                                    <th>{t('membre.public.col_adresse')}</th>
                                    <th>{t('membre.public.col_localite')}</th>
                                    <th>{t('membre.public.col_canton')}</th>
                                    <th>{t('membre.public.col_type_membre')}</th>
                                    <th>{t('membre.public.col_site_web')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRows.map((row) => (
                                    <MemberRow
                                        key={`${row.member_id}-${row.canton}-${row.localite}`}
                                        row={row}
                                        restBaseUrl={initData.restBaseUrl}
                                        restNonce={initData.restNonce}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {pageSize > 0 && totalPages > 1 && (
                        <nav className="gas-public-members__pagination" aria-label={t('membre.public.pagination_label')}>
                            <GasButton type="button" variant="secondary" disabled={safePage <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>
                                {t('common.previous')}
                            </GasButton>
                            <span>{t('membre.public.pagination_status', { page: safePage, total: totalPages })}</span>
                            <GasButton type="button" variant="secondary" disabled={safePage >= totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}>
                                {t('common.next')}
                            </GasButton>
                        </nav>
                    )}
                </>
            )}
        </div>
    );
}

function SuggestionList({
    suggestions,
    loading,
    onPick,
}: {
    suggestions: PublicMembersSuggestion[];
    loading: boolean;
    onPick: (suggestion: PublicMembersSuggestion) => void;
}) {
    const { t } = useGasI18n();

    return (
        <div className="gas-public-members__suggestions" role="listbox">
            {loading && <div className="gas-public-members__suggestion">{t('common.loading')}</div>}
            {!loading && suggestions.length === 0 && (
                <div className="gas-public-members__suggestion">{t('membre.public.no_suggestion')}</div>
            )}
            {!loading && suggestions.map((suggestion) => (
                <button
                    key={`${suggestion.slug}-${suggestion.label}`}
                    type="button"
                    className="gas-public-members__suggestion"
                    onClick={() => onPick(suggestion)}
                >
                    {suggestion.label}
                </button>
            ))}
        </div>
    );
}

function MemberRow({
    row,
    restBaseUrl,
    restNonce,
}: {
    row: PublicMemberRow;
    restBaseUrl: string;
    restNonce?: string;
}) {
    const { t } = useGasI18n();
    const fullName = `${row.prenom} ${row.nom}`.trim();
    const city = [row.npa, row.localite].filter(Boolean).join(' ');
    const missing = <span className="gas-public-members__muted">{t('membre.public.missing_value')}</span>;
    const genreLabel = row.genre === 'homme'
        ? t('membre.search.defaults.genre_homme')
        : row.genre === 'femme'
            ? t('membre.search.defaults.genre_femme')
            : '';

    const trackProfileClick = () => {
        if (row.member_id > 0) {
            trackMemberClick(restBaseUrl, {
                memberId: row.member_id,
                source: 'list_card',
                pageContext: 'list',
            }, restNonce);
        }
    };

    return (
        <tr>
            <td>
                {row.photo_url ? (
                    <img className="gas-public-members__photo" src={row.photo_url} alt={fullName} loading="lazy" />
                ) : (
                    missing
                )}
            </td>
            <td>{row.prenom || missing}</td>
            <td>
                {row.profile_url ? (
                    <a className="gas-public-members__name" href={row.profile_url} onClick={trackProfileClick}>
                        {row.nom || fullName}
                    </a>
                ) : (
                    row.nom || missing
                )}
            </td>
            <td>{genreLabel || missing}</td>
            <td>{row.nom_cabinet || missing}</td>
            <td>{row.adresse || missing}</td>
            <td>{city || missing}</td>
            <td>{row.canton ? <GasBadge tone="muted">{row.canton}</GasBadge> : missing}</td>
            <td>{row.type_membre ? <GasBadge tone="success">{row.type_membre}</GasBadge> : missing}</td>
            <td>
                {row.website_url ? (
                    <a href={row.website_url} target="_blank" rel="noopener noreferrer">
                        {t('membre.public.website_link')}
                    </a>
                ) : (
                    missing
                )}
            </td>
        </tr>
    );
}
